import { useMemo } from 'react';

import { dashTabQueryKey } from '@entities/dashboard/model/queries';
import { filterParamsToQueryKeys } from '@entities/eventJournal/lib';
import { queryParamsAdapterToRequest } from '@entities/eventJournal/lib';
import { EventJournalQueries } from '@entities/eventJournal/model/queries';
import { useNuqsQueryState } from '@shared/hooks';

export const useGetAllQueryParams = () => {
    const { state: search } = useNuqsQueryState(EventJournalQueries.search);
    const { state: sort } = useNuqsQueryState(EventJournalQueries.sort);
    const { state: dateFrom } = useNuqsQueryState(EventJournalQueries.dateFrom);
    const { state: dateTo } = useNuqsQueryState(EventJournalQueries.dateTo);
    const { state: type } = useNuqsQueryState(EventJournalQueries.type);
    const { state: estimate } = useNuqsQueryState(EventJournalQueries.estimate);
    const { state: authors } = useNuqsQueryState(EventJournalQueries.authors);
    const { state: dashTab } = useNuqsQueryState(dashTabQueryKey);

    const rawSearch = typeof window !== 'undefined' ? window.location.search : '';
    const sp = useMemo(() => new URLSearchParams(rawSearch), [rawSearch]);

    const params = useMemo(
        () =>
            Object.fromEntries(
                (
                    [
                        [EventJournalQueries.search, search],
                        [EventJournalQueries.sort, sort],
                        [EventJournalQueries.dateFrom, dateFrom],
                        [EventJournalQueries.dateTo, dateTo],
                        [EventJournalQueries.type, type],
                        [EventJournalQueries.estimate, estimate],
                        [EventJournalQueries.authors, authors],
                        [dashTabQueryKey, dashTab],
                    ] as const
                )
                    .filter(([k]) => sp.has(k))
                    .map(([k, v]) => [k, String(v ?? sp.get(k) ?? '')])
            ),
        [sp, search, sort, dateFrom, dateTo, type, estimate, authors, dashTab]
    );

    const queryParams = useMemo(() => {
        const cleaned = Object.fromEntries(
            Object.entries(params).filter(([, v]) => String(v).trim().length > 0)
        );
        return filterParamsToQueryKeys(cleaned);
    }, [params]);

    const adaptedQueryParams = useMemo(
        () => queryParamsAdapterToRequest(queryParams),
        [queryParams]
    );

    return { queryParams, params, adaptedQueryParams };
};
